import React from 'react';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

const OrganizacionDetail = ({ organizacion, estadisticas = null }) => {
  const DetailRow = ({ icon: Icon, label, value, valueClassName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-start">
        <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
        <div className="flex-1">
          <dt className="text-sm font-medium text-gray-500">{label}</dt>
          <dd className={`mt-1 text-sm ${valueClassName || 'text-gray-900'}`}>
            {value || 'N/A'}
          </dd>
        </div>
      </div>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header con estado */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-primary-600 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {organizacion.nombre}
            </h3>
            <p className="text-sm text-gray-500">ID: {organizacion.id_organizacion}</p>
          </div>
        </div>

        <div>
          {organizacion.activo ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-4 w-4 mr-1" />
              Activa
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              <XCircle className="h-4 w-4 mr-1" />
              Inactiva
            </span>
          )}
        </div>
      </div>

      {/* Información básica */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Información de Contacto
        </h4>
        <dl className="divide-y divide-gray-200">
          <DetailRow
            icon={Building2}
            label="Nombre"
            value={organizacion.nombre}
          />
          <DetailRow
            icon={MapPin}
            label="Dirección"
            value={organizacion.direccion}
          />
          <DetailRow icon={Phone} label="Teléfono" value={organizacion.telefono} />
          <DetailRow
            icon={Mail}
            label="Email de contacto"
            value={organizacion.email_contacto}
          />
        </dl>
      </div>

      {/* Descripción */}
      {organizacion.descripcion && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Descripción
          </h4>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {organizacion.descripcion}
          </p>
        </div>
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Estadísticas
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {estadisticas.total_usuarios || 0}
              </p>
              <p className="text-xs text-gray-600">Usuarios</p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {estadisticas.total_evaluaciones || 0}
              </p>
              <p className="text-xs text-gray-600">Evaluaciones</p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {estadisticas.total_codigos || 0}
              </p>
              <p className="text-xs text-gray-600">Códigos</p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {estadisticas.total_denuncias || 0}
              </p>
              <p className="text-xs text-gray-600">Denuncias</p>
            </div>
          </div>
        </div>
      )}

      {/* Información de auditoría */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Información de Auditoría
        </h4>
        <dl className="divide-y divide-gray-200">
          <DetailRow
            icon={Calendar}
            label="Fecha de creación"
            value={formatDate(organizacion.fecha_creacion)}
          />
          <DetailRow
            icon={Calendar}
            label="Última actualización"
            value={formatDate(organizacion.fecha_actualizacion)}
          />
        </dl>
      </div>
    </div>
  );
};

export default OrganizacionDetail;
